import { Box, Typography, Paper, Tooltip, LinearProgress, Chip, Button, Stack } from "@mui/material";
import PaidIcon from "@mui/icons-material/Paid";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import MoneyOffCsredIcon from "@mui/icons-material/MoneyOffCsred";
import { generateButtonSx } from "../../../utils/elementor-style-override";

export const columns = [
	{
		field: "agent_name",
		headerName: "Direct Recruit",
		flex: 1,
		minWidth: 180,
		align: "center",
		headerAlign: "center",
		renderCell: (params) => {
			// The backend already replaced an opted-out agent's name with a placeholder,
			// so a chip saying the same thing would just repeat it. Grey it instead and
			// let the tooltip carry the explanation.
			const isPlaceholder = Number(params.row.is_activated ?? 1) !== 1;

			return (
				<Stack direction="row" alignItems="center" justifyContent="center" gap={0.75} sx={{ width: "100%" }}>
					<Tooltip
						title={
							isPlaceholder
								? "This agent opted out and has been removed from the portal. The row remains because the network they built still counts toward this progress."
								: ""
						}
					>
						<Typography
							fontSize={13}
							noWrap
							color={isPlaceholder ? "text.disabled" : "text.primary"}
							sx={{ fontStyle: isPlaceholder ? "italic" : "normal" }}
						>
							{params.row.agent_name}
						</Typography>
					</Tooltip>
				</Stack>
			);
		},
	},
	{
		field: "progress",
		headerName: "Current Members",
		flex: 1,
		minWidth: 180,
		align: "center",
		headerAlign: "center",
		renderCell: (params) => (
			<Stack direction="row" alignItems="center" justifyContent="center" gap={0.75} sx={{ width: "100%" }}>
				<Typography fontSize={13}>{Number(params.row.progress ?? 0).toLocaleString()}</Typography>
				{Number(params.row.is_activated ?? 1) !== 1 && (
					<Tooltip title="Network members only. Their own book doesn't count while opted out.">
						<Typography fontSize={11} color="text.disabled">
							(network only)
						</Typography>
					</Tooltip>
				)}
			</Stack>
		),
	},
	{
		field: "milestone",
		headerName: "Milestone",
		flex: 1,
		minWidth: 220,
		align: "center",
		headerAlign: "center",
		renderCell: (params) => {
			const current = params.row.progress;
			const target = params.row.target;
			const progress = Math.min((current / target) * 100, 100);

			const tooltipContent = (
				<Paper sx={{ p: 1.5, minWidth: 180, borderRadius: 2 }}>
					<Typography sx={{ fontSize: 12, fontWeight: 600, mb: 1 }}>Milestone Details</Typography>
					<Typography sx={{ fontSize: 11, color: "#666" }}>
						Target: <b>{target}</b>
					</Typography>
					<Typography sx={{ fontSize: 11, color: "#666" }}>
						Current: <b>{current}</b>
					</Typography>
					<Typography sx={{ fontSize: 11, color: "#666" }}>
						Remaining: <b>{Math.max(target - current, 0)}</b>
					</Typography>
					<Typography sx={{ fontSize: 11, mt: 1, fontWeight: 600 }}>{current >= target ? "Completed 🎉" : "In progress"}</Typography>
				</Paper>
			);

			return (
				<Tooltip
					title={tooltipContent}
					arrow
					placement="top"
					slotProps={{
						tooltip: { sx: { bgcolor: "transparent !important", boxShadow: "none !important", p: 0 } },
						arrow: { sx: { color: "transparent !important" } },
					}}
				>
					<Box sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", px: 3, py: 1.5 }}>
						<LinearProgress
							variant="determinate"
							value={progress}
							sx={{
								width: "100%",
								height: 8,
								borderRadius: 5,
								backgroundColor: "rgba(0,0,0,0.06)",
								"& .MuiLinearProgress-bar": {
									backgroundColor: current >= target ? "#2e7d32" : "#1D54A2",
									borderRadius: 5,
								},
							}}
						/>
						<Typography sx={{ fontSize: 11, mt: 0.5, color: "#888" }}>
							{current} / {target}
						</Typography>
					</Box>
				</Tooltip>
			);
		},
	},
	{
		field: "reward",
		headerName: "Recruit Reward",
		flex: 1,
		minWidth: 140,
		align: "center",
		headerAlign: "center",
		renderCell: (params) => (
			<Box
				sx={{
					height: "100%",
					width: "100%",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<Typography fontWeight={700} fontSize={14} color="#1D54A2">
					${params.row.reward.toLocaleString()}
				</Typography>
			</Box>
		),
	},
	{
		field: "status",
		headerName: "Status",
		flex: 1,
		minWidth: 160,
		align: "center",
		headerAlign: "center",
		renderCell: (params) => {
			const statusMap = {
				for_approval: {
					label: "For Approval",
					icon: <HourglassEmptyIcon />,
					borderColor: "#1D54A2",
					bg: "rgba(29, 84, 162, 0.08)",
					color: "#1D54A2",
				},
				approved: {
					label: "Approved",
					icon: <PaidIcon />,
					borderColor: "#2e7d32",
					bg: "rgba(46, 125, 50, 0.08)",
					color: "#2e7d32",
				},
				rejected: {
					label: "Rejected",
					icon: <MoneyOffCsredIcon />,
					borderColor: "#d32f2f",
					bg: "rgba(211, 47, 47, 0.08)",
					color: "#d32f2f",
				},
				pending: {
					label: "Pending",
					icon: <HourglassEmptyIcon />,
					borderColor: "#ed6c02",
					bg: "rgba(237, 108, 2, 0.08)",
					color: "#ed6c02",
				},
			};

			const style = statusMap[params.row.status] ?? statusMap.pending;

			return (
				<Chip
					icon={style.icon}
					label={style.label}
					size="small"
					variant="outlined"
					sx={{
						px: 1.5,
						fontWeight: 600,
						borderWidth: "1.5px",
						borderColor: style.borderColor,
						backgroundColor: style.bg,
						color: style.color,
						"& .MuiChip-icon": { color: style.color },
					}}
				/>
			);
		},
	},
];

export const historyColumns = ({ onApprove, onReject, onViewDetails, isAgent }) => [
	{
		field: "bonus_amount",
		headerName: "Bonus Amount",
		flex: 1,
		minWidth: 160,
		align: "center",
		headerAlign: "center",
		renderCell: (params) => (
			<Box
				sx={{
					height: "100%",
					width: "100%",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<Typography fontWeight={700} fontSize={14} color="#1D54A2">
					${params.row.bonus_amount.toLocaleString()}
				</Typography>
			</Box>
		),
	},
	{
		field: "milestone",
		headerName: "Milestone",
		flex: 1,
		minWidth: 200,
		align: "center",
		headerAlign: "center",
		renderCell: (params) => (
			<Box
				sx={{
					height: "100%",
					width: "100%",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					gap: 0.75,
				}}
			>
				<EmojiEventsIcon sx={{ fontSize: 18, color: "#f59e0b" }} />
				<Typography fontWeight={600} fontSize={13} color="#333">
					{params.row.milestone} Active Members
				</Typography>
			</Box>
		),
	},
	{
		field: "paid_date",
		headerName: "Paid Date",
		flex: 1,
		minWidth: 160,
		align: "center",
		headerAlign: "center",
		renderCell: (params) => (
			<Box
				sx={{
					height: "100%",
					width: "100%",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<Typography fontSize={13} color="#555">
					{params.row.paid_date ?? "—"}
				</Typography>
			</Box>
		),
	},
	{
		field: "status",
		headerName: "Status",
		flex: 1,
		minWidth: 150,
		align: "center",
		headerAlign: "center",
		renderCell: (params) => {
			const statusMap = {
				for_approval: {
					label: "For Approval",
					icon: <HourglassEmptyIcon />,
					borderColor: "#1D54A2",
					bg: "rgba(29, 84, 162, 0.08)",
					color: "#1D54A2",
				},
				approved: {
					label: "Approved",
					icon: <PaidIcon />,
					borderColor: "#2e7d32",
					bg: "rgba(46, 125, 50, 0.08)",
					color: "#2e7d32",
				},
				rejected: {
					label: "Rejected",
					icon: <MoneyOffCsredIcon />,
					borderColor: "#d32f2f",
					bg: "rgba(211, 47, 47, 0.08)",
					color: "#d32f2f",
				},
				pending: {
					label: "Pending",
					icon: <HourglassEmptyIcon />,
					borderColor: "#ed6c02",
					bg: "rgba(237, 108, 2, 0.08)",
					color: "#ed6c02",
				},
			};

			const style = statusMap[params.row.status] ?? statusMap.pending;

			return (
				<Chip
					icon={style.icon}
					label={style.label}
					size="small"
					variant="outlined"
					sx={{
						px: 1.5,
						fontWeight: 600,
						borderWidth: "1.5px",
						borderColor: style.borderColor,
						backgroundColor: style.bg,
						color: style.color,
						"& .MuiChip-icon": { color: style.color },
					}}
				/>
			);
		},
	},
	...(!isAgent
		? [
				{
					field: "approval",
					headerName: "Action",
					flex: 1,
					minWidth: 220,
					align: "center",
					headerAlign: "center",
					sortable: false,
					renderCell: (params) => {
						const status = params.row.status;

						// approved/rejected
						if (status === "approved" || status === "rejected") {
							return (
								<Stack direction="row" justifyContent="center" pt={1}>
									<Button
										size="small"
										onClick={() => onViewDetails?.(params.row)}
										sx={{
											px: 2,
											color: "#FFFFFF",
											backgroundColor: "#1D54A2 !important",
											...generateButtonSx(
												{ bg: "#F4F4F5 !important", text: "#1D54A2" },
												{ bg: "#F4F4F5 !important", text: "#1D54A2" }
											),
										}}
									>
										View Details
									</Button>
								</Stack>
							);
						}

						// To Be Approved
						if (status === "for_approval") {
							return (
								<Stack direction="row" justifyContent="center" gap={2} pt={1}>
									<Button
										size="small"
										variant="contained"
										onClick={() => onApprove?.(params.row)}
										sx={{
											backgroundColor: "#1D54A2 !important",
											...generateButtonSx(
												{ bg: "#F4F4F5 !important", text: "#1D54A2" },
												{ bg: "#F4F4F5 !important", text: "#1D54A2" }
											),
										}}
									>
										Approve
									</Button>

									<Button
										size="small"
										variant="outlined"
										onClick={() => onReject?.(params.row)}
										color="error"
										sx={{
											backgroundColor: "transparent !important",
											...generateButtonSx(
												{ bg: "#FEF2F2 !important", text: "#DC2626" },
												{ bg: "#FEE2E2 !important", text: "#DC2626" }
											),
										}}
									>
										Reject
									</Button>
								</Stack>
							);
						}

						if (status === "pending") {
							return (
								<Stack justifyContent="center" pt={1.5}>
									<Typography>Not Yet Achieved</Typography>
								</Stack>
							);
						}

						return null;
					},
				},
			]
		: []),
];
